// src/context/AuthContext.js
// ===============================================
// 認証・役割・権限(RBAC)のコンテキスト
// - Firebase Auth の状態を監視
// - users コレクションからアプリユーザー情報を取得（authUid 経由）
// - roles コレクションから役割と permissions を取得
// - can(resource, action) ヘルパーを提供
//   resource: "dashboard" など
//   action  : "view" | "edit" | "delete"
// ===============================================

import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase-config';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';

const AuthContext = createContext();

// 既定で閲覧を許可する画面（役割が未整備な初期段階の退避策）
const DEFAULT_FALLBACK_VIEW_ALLOW = new Set(['dashboard', 'myPage']);

export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null);       // Firebase Auth ユーザー
  const [currentUser, setCurrentUser] = useState(null); // アプリユーザー(users)
  const [userRole, setUserRole] = useState(null);       // 役割ドキュメント(roles)
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // permissions を正規化：{[resource]: {view, edit, delete}}
  const permissionMap = useMemo(() => {
    const src = userRole?.permissions || {};
    const out = {};
    Object.keys(src).forEach((k) => {
      const p = src[k] || {};
      out[k] = {
        view: !!p.view,
        edit: !!p.edit,
        delete: !!p.delete,
      };
    });
    return out;
  }, [userRole]);

  // ヘルパー: 権限判定
  const can = useMemo(() => {
    // action: "view" | "edit" | "delete"
    return (resource, action = 'view') => {
      if (!resource) return false;
      const key = String(resource);
      const p = permissionMap[key];

      // 権限未設定時のフェイルセーフ（ダッシュボード/マイページは閲覧OK）
      if (!p) {
        return action === 'view' && DEFAULT_FALLBACK_VIEW_ALLOW.has(key);
      }
      return !!p[action];
    };
  }, [permissionMap]);

  const canView   = (res) => can(res, 'view');
  const canEdit   = (res) => can(res, 'edit');
  const canDelete = (res) => can(res, 'delete');

  // Auth 監視 → Firestore からユーザー情報と役割を取得
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      try {
        if (user) {
          setAuthUser(user);

          // users コレクションから authUid 紐付け
          const q = query(collection(db, 'users'), where('authUid', '==', user.uid));
          const snap = await getDocs(q);

          if (!snap.empty) {
            const userDoc = snap.docs[0];
            const userData = { ...userDoc.data(), id: userDoc.id };
            setCurrentUser(userData);

            // 役割の取得（存在しない場合は null）
            if (userData.roleId) {
              const rDoc = await getDoc(doc(db, 'roles', userData.roleId));
              setUserRole(rDoc.exists() ? { ...rDoc.data(), id: rDoc.id } : null);
            } else {
              setUserRole(null);
            }
          } else {
            // Auth には存在するが Firestore users にない
            setCurrentUser(null);
            setUserRole(null);
            console.warn('User exists in Auth but not in Firestore (users).');
          }
        } else {
          setAuthUser(null);
          setCurrentUser(null);
          setUserRole(null);
        }
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // ログイン（永続化：ローカル or セッション）
  const login = async (email, password, remember = true) => {
    try {
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
      await signInWithEmailAndPassword(auth, email, password);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error.code);
      return { success: false, code: error.code };
    }
  };

  // ログアウト
  const logout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const value = {
    authUser,
    currentUser,
    userRole,
    permissions: permissionMap, // 実態のマップも公開
    loading,
    login,
    logout,
    // 権限ヘルパー
    can,
    canView,
    canEdit,
    canDelete,
  };

  // ローディング中は子要素を描画しない（ちらつき防止）
  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
